import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

interface PaginationStateOptions {
  pageSize?: number
  paramName?: string
  defaultPage?: number
}

export function usePaginationState({
  pageSize = 50,
  paramName = "page",
  defaultPage = 1,
}: PaginationStateOptions) {
  const [searchParams, setSearchParams] = useSearchParams()

  const currentPage = useMemo(() => {
    const pageFromUrl = searchParams.get(paramName)
    if (pageFromUrl) {
      const parsedPage = parseInt(pageFromUrl, 10)
      return isNaN(parsedPage) || parsedPage < 1 ? defaultPage : parsedPage
    }
    return defaultPage
  }, [searchParams, paramName, defaultPage])

  const setPage = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        if (page === defaultPage) {
          newParams.delete(paramName)
        } else {
          newParams.set(paramName, page.toString())
        }
        return newParams
      })
    },
    [setSearchParams, paramName, defaultPage],
  )

  const paginationState = useMemo(
    () => ({
      pageIndex: currentPage - 1,
      pageSize,
    }),
    [currentPage, pageSize],
  )

  return {
    pageIndex: currentPage - 1,
    pageSize,
    setPage,
    currentPage,
    paginationState,
  }
}
